/**
 * Keyframe properties at the operation boundary (spec 036): the names and
 * units the keyframe operations take and `getSprite`'s `keys` give, mapped
 * to spec 006's stored properties (the flips by spec 038). Every animatable sprite property of 006
 * has a name here; none is left out.
 */
import type { Keyframe, KeyframeEasing, Orientation } from '../@types';

/** The operation names of 006's keyframe properties, in the order the reads list them. */
export type KeyProperty = 'x' | 'y' | 'scale' | 'rotation' | 'opacity' | 'hingeX' | 'hingeY' | 'state' | 'flipX' | 'flipY';

/** Operation name → stored property, in the reads' order (D-12, `KeyView`). */
export const KEY_PROPERTIES: readonly (readonly [KeyProperty, string])[] = Object.freeze([
  ['x', 'position.x'],
  ['y', 'position.y'],
  ['scale', 'transform.scale'],
  ['rotation', 'transform.rotation'],
  ['opacity', 'opacity'],
  ['hingeX', 'hinge.x'],
  ['hingeY', 'hinge.y'],
  ['state', 'state'],
  ['flipX', 'transform.flipX'],
  ['flipY', 'transform.flipY'],
] as const);

const TO_STORED = new Map<string, string>(KEY_PROPERTIES.map(([n, p]) => [n, p]));
const TO_NAME = new Map<string, KeyProperty>(KEY_PROPERTIES.map(([n, p]) => [p, n]));

/** The stored property of an operation name. */
export const storedProperty = (name: KeyProperty): string => TO_STORED.get(name)!;
/** The operation name of a stored property, or undefined for one 006 does not animate. */
export const keyPropertyName = (property: string): KeyProperty | undefined => TO_NAME.get(property);

/** Hinge keys are 0–100 stored and 0–1 at the boundary (006, D-2). */
const isHinge = (property: string) => property === 'hinge.x' || property === 'hinge.y';
/** Flip keys are `1` / `0` stored and `true` / `false` at the boundary (spec 038 D-3). */
const isFlip = (property: string) => property === 'transform.flipX' || property === 'transform.flipY';

/** A boundary value as stored. */
export const toStoredValue = (property: string, value: number | string | boolean): number | string => {
  if (typeof value === 'boolean') return value ? 1 : 0;
  return isHinge(property) && typeof value === 'number' ? value * 100 : value;
};

/** A stored value at the boundary (hinge 0–1, rounded to 4 decimals to undo the ×100; a flip as a boolean). */
export const toBoundaryValue = (property: string, value: number | string): number | string | boolean => {
  if (isFlip(property)) return value !== 0 && value !== '0';
  return isHinge(property) && typeof value === 'number' ? Math.round(value * 100) / 10000 : value;
};

/** One keyframe as `getSprite` lists it, in the operation units (D-2): `at` seconds, hinge 0–1. */
export interface KeyView {
  at: number;
  property: KeyProperty;
  /** A number, a state name, or for a flip `true` / `false` (spec 038). */
  value: number | string | boolean;
  ease?: KeyframeEasing;
  orientation?: Orientation;
}

const TAG_ORDER: Record<string, number> = { '': 0, desktop: 1, portrait: 2, landscape: 3 };
const PROPERTY_ORDER = new Map<string, number>(KEY_PROPERTIES.map(([, p], i) => [p, i]));

/**
 * A track's keys as `KeyView`s: ordered by property, then untagged, desktop,
 * portrait, landscape, then time. Keys of a property 006 does not animate
 * are left out. Never an id or a track id.
 */
export function keyViews(track: readonly Keyframe[]): KeyView[] {
  return track
    .filter((k) => keyPropertyName(k.property) !== undefined)
    .slice()
    .sort((a, b) => (PROPERTY_ORDER.get(a.property)! - PROPERTY_ORDER.get(b.property)!)
      || (TAG_ORDER[a.orientation ?? ''] - TAG_ORDER[b.orientation ?? ''])
      || (a.time - b.time))
    .map((k) => ({
      at: k.time / 1000,
      property: keyPropertyName(k.property)!,
      value: toBoundaryValue(k.property, k.value),
      ...(k.easing && !isFlip(k.property) ? { ease: k.easing } : {}),
      ...(k.orientation ? { orientation: k.orientation } : {}),
    }));
}
